import { makeStyles, styled } from "@mui/styles";
import classnames from "classnames";
import queryString from "query-string";
import React, { SyntheticEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Box, BoxProps, Tab, Tabs, Theme } from "@mui/material";

import SkeletonWrapper from "components/core/SkeletonWrapper";
import { TabPanelType } from "types";

import TabPanel from "./TabPanel";

const FullWidthBox = styled(Box)({
  width: "100%",
});

interface BasicTabsProps {
  sx?: BoxProps["sx"];
  tabSx?: BoxProps["sx"];
  className?: string;
  queryKey?: string; // for changing tab in url with location.hash
  noUsingQueryKey?: boolean;
  containerDisabled?: boolean;
  loading?: boolean;
  wrapped?: boolean;
  defaultTab?: string;
  tabItems: string[];
  tabPanels: TabPanelType[];
  actionComponent?: React.ReactNode;
}

export default function BasicTabs({
  sx,
  tabSx,
  className,
  queryKey = "tab",
  noUsingQueryKey = false,
  containerDisabled = false,
  loading = false,
  wrapped = false,
  defaultTab,
  tabItems,
  tabPanels,
  actionComponent,
}: BasicTabsProps) {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();

  const navigate = useNavigate();

  const parsedQuery = queryString.parse(window.location.search);

  const queryValue = parsedQuery[queryKey] ?? "";

  const [selectedTab, setSelectedTab] = useState<any>(defaultTab);

  const handleChangeTabSearch = (value: number | string) => {
    if (noUsingQueryKey) return;

    const queryObj = { ...parsedQuery, [queryKey]: value };

    const stringified = queryString.stringify(queryObj);

    navigate(
      {
        search: `?${stringified}`,
      },
      {
        replace: true,
      },
    );
  };

  const handleChange = (event: SyntheticEvent, newValue: number | string) => {
    setSelectedTab(newValue);
    handleChangeTabSearch(newValue);
  };

  useEffect(() => {
    if (queryValue || noUsingQueryKey) return;

    setSelectedTab(queryValue);
    handleChangeTabSearch(selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    if (!queryValue || noUsingQueryKey) return;

    setSelectedTab(queryValue);
    handleChangeTabSearch(queryValue as string);
  }, [queryValue]);

  const a11yProps = (index: number | string) => ({
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  });

  return (
    <FullWidthBox sx={sx} className={classnames(classes.root, className)}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ borderBottom: 1, borderColor: "divider", backgroundColor: "#fff", ...tabSx }}
      >
        {containerDisabled ? (
          <Tabs
            // eslint-disable-next-line jsx-a11y/aria-role
            role="tabs"
            aria-label="basic tabs"
            scrollButtons="auto"
            allowScrollButtonsMobile
            variant="scrollable"
            data-cy="tab-list"
            value={selectedTab || tabPanels[0]?.id}
            onChange={handleChange}
            className={classes.tab}
          >
            {tabItems?.map((item, index) => (
              <Tab
                key={tabPanels[index]?.id}
                sx={{
                  textTransform: "capitalize",
                  textAlign: "center",
                  fontSize: "12px",
                  minWidth: "150px",
                }}
                wrapped={wrapped}
                label={<SkeletonWrapper loading={loading}>{item}</SkeletonWrapper>}
                value={tabPanels[index]?.id}
                data-cy={item.split(" ").join("")}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...a11yProps(tabPanels[index]?.id)}
              />
            ))}
          </Tabs>
        ) : (
          <Box
            sx={{
              maxWidth: "100%",
              "& button.MuiTab-wrapped": {
                width: 100,
                "& > div": {
                  height: "initial",
                },
              },
            }}
          >
            <Tabs
              // eslint-disable-next-line jsx-a11y/aria-role
              role="tabs"
              aria-label="basic tabs"
              scrollButtons="auto"
              allowScrollButtonsMobile
              variant="scrollable"
              data-cy="tab-list"
              value={selectedTab}
              onChange={handleChange}
              className={classes.tab}
            >
              {tabItems?.map((item, index) => (
                <Tab
                  key={tabPanels[index]?.id}
                  sx={{
                    textTransform: "capitalize",
                    textAlign: "center",
                    fontSize: "12px",
                    minWidth: "150px",
                  }}
                  wrapped={wrapped}
                  label={<SkeletonWrapper loading={loading}>{item}</SkeletonWrapper>}
                  value={tabPanels[index]?.id}
                  data-cy={item.split(" ").join("")}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...a11yProps(tabPanels[index]?.id)}
                />
              ))}
            </Tabs>
          </Box>
        )}
        {actionComponent && actionComponent}
      </Box>

      {tabPanels?.map((panel) => (
        <TabPanel key={panel?.id} index={panel?.id} value={selectedTab}>
          {panel?.component}
        </TabPanel>
      ))}
    </FullWidthBox>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  tab: {
    padding: "0 !important",
    "& button": {
      width: "auto",
    },
    "& .MuiTabs-indicator": {
      backgroundColor: theme.palette.primary.main,
    },
  },
}));
